/**
 * CLONE REBRAND FILE — keep in sync with src/assets/css/theme.css
 *
 * Checklist for a new project clone:
 * 1. Set APP.* (name, title, copyright year, developerCredit)
 * 2. Set BRAND.* (primary, accents, danger)
 * 3. Set BUTTONS.* / matching --btn-* in theme.css (Add/Excel/PDF/Save/Cancel/Close)
 * 4. Set TYPOGRAPHY.* / --font-* (family + sizes)
 * 5. Swap logo assets (Sidebar / Login) + account icon silhouette (user_icon.png)
 * 6. Adjust GRADIENTS / TOP_10_RANK_COLORS if needed
 * 7. Smoke: Login, list Add, form Save/Close, Report Excel/PDF, Dashboard cards, navbar account icon
 */

/** App identity — change once per clone (title, footer, login copy, labels) */
export const APP = {
  name: "RMBF",
  shortName: "RMBF",
  fullName: "Rotary Means Business Fellowship",
  /** Browser tab title */
  documentTitle: "RMBF - Backend",
  /** Footer copyright year */
  copyrightYear: 2026,
  get copyright() {
    return `Copyright © ${this.copyrightYear}. ${this.name}. All Rights Reserved.`;
  },
  /** Login hero supporting line — use {name} placeholder */
  loginTagline:
    "The all-in-one platform for {name} — manage members, track referrals, and grow your network effortlessly.",
  logoAlt: "{name} Logo",
  memberLabel: "{name} Member",
  /**
   * Footer + Login “Design & Developed By” credit.
   * Toggle `show` to enable/disable in both places.
   */
  developerCredit: {
    show: true,
    url: "https://www.innoblitz.global/",
    label: "Design & Developed By",
    company: "Innoblitz Technologies And Systems Pvt Ltd",
    color: "#0c3c7c",
    fontSize: "15px",
  },
  /** Navbar center live clock — toggle `show` to hide/show */
  navbarClock: {
    show: true,
  },
};

export const appText = (template) =>
  String(template).replace(/\{name\}/g, APP.name);

export const BRAND = {
  primary: "#0c3c7c",
  primaryRgb: [12, 60, 124],
  primaryArgb: "FF0C3C7C",
  primaryLight: "#1a5bb5",
  primaryDark: "#082650",
  primaryMid: "#0f4a99",
  accentYellow: "#ffd54f",
  danger: "#dc2626",
  dangerHover: "#b91c1c",
  white: "#FFFFFF",
  whiteArgb: "FFFFFFFF",
  textOnPrimary: "#FFFFFF",
  mutedBg: "#efefef",
  mutedBorder: "#d0def0",
  mutedText: "#3d4152",
  goBackBg: "#eaf0f8",
  /** Navbar / default account avatar circle — mirrors --brand-account-icon-bg */
  get accountIconBg() {
    return this.primary;
  },
  /** Modal top-right X button — mirrors --brand-modal-dismiss-bg */
  get modalDismissBg() {
    return this.primary;
  },
  /** Dashboard page title icon — mirrors --brand-dashboard-title-icon (sidebar color) */
  get dashboardTitleIcon() {
    return this.primary;
  },
};

export const TYPOGRAPHY = {
  fontFamily: "'Poppins', sans-serif",
  xs: "12px",
  sm: "13px",
  md: "14px",
  base: "15px",
  lg: "16px",
  xl: "17px",
  "2xl": "20px",
  "3xl": "22px",
  button: "13px",
  buttonWeight: 400,
  buttonWeightBold: 700,
  pageTitle: "24px",
  tableHeader: "15px",
};

/** Semantic button configs — clone projects tweak here; CSS vars drive visuals */
export const BUTTONS = {
  add: {
    bg: BRAND.primary,
    color: BRAND.white,
    hoverBg: BRAND.primaryDark,
    border: "none",
    radius: "8px",
    fontSize: TYPOGRAPHY.button,
    fontWeight: TYPOGRAPHY.buttonWeight,
    padding: "6px 12px",
    minWidth: "64px",
    className: "btn rounded-pill btn-primary ibutton",
  },
  excel: {
    bg: BRAND.primary,
    color: BRAND.white,
    hoverBg: BRAND.primaryDark,
    className: "btn rounded-pill btn-primary excel-btn ibutton",
    fontSize: TYPOGRAPHY.button,
  },
  pdf: {
    bg: BRAND.primary,
    color: BRAND.white,
    hoverBg: BRAND.primaryDark,
    className: "btn rounded-pill btn-primary pdf-btn ibutton",
    fontSize: TYPOGRAPHY.button,
  },
  save: {
    bg: BRAND.primary,
    color: BRAND.white,
    border: BRAND.primary,
    className: "btn btn-success isubmit_button",
    fontSize: TYPOGRAPHY.button,
    fontWeight: TYPOGRAPHY.buttonWeightBold,
  },
  cancel: {
    bg: "#ccc",
    color: "#000",
    border: "#ccc",
    className: "btn btn-primary iclose_btn",
    fontSize: TYPOGRAPHY.button,
  },
  close: {
    bg: "#ccc",
    color: "#000",
    border: "#ccc",
    className: "btn btn-secondary ibutton",
    fontSize: TYPOGRAPHY.button,
  },
};

export const TOP_10_RANK_COLORS = [
  "#0c3c7c", // rank 1 — navy primary
  "#0c3c7c", // rank 2
  "#0c3c7c", // rank 3
  "#0c3c7c", // rank 4
  "#0c3c7c", // rank 5
  "#0c3c7c", // rank 6
  "#0c3c7c", // rank 7
  "#0c3c7c", // rank 8
  "#0c3c7c", // rank 9
  "#0c3c7c", // rank 10
];

export const getTop10RankColor = (index) =>
  TOP_10_RANK_COLORS[Math.min(index, 9)];

export const getTop10RankTextColor = (index) =>
  index < 0 ? "#0c3c7c" : BRAND.white;

export const GRADIENTS = {
  birthdayCard: BRAND.primary,
  meetingAbsentees: BRAND.primary,
  pendingMeetingFees: BRAND.primary,
  topAchieversHeader: BRAND.primary,
  analyticsCard: BRAND.primary,
  /** Stat cards grid background (Dashboard.css .analytics-grid-card) */
  analyticsGridCard: BRAND.primary,
  loginButton: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)`,
};

export const SHADOWS = {
  insightCard: `0 6px 16px rgba(${BRAND.primaryRgb.join(", ")}, 0.25)`,
};

export const rgbaPrimary = (alpha) =>
  `rgba(${BRAND.primaryRgb.join(", ")}, ${alpha})`;

/** Chapter-wise pie / chart series colors (opacity steps of primary) */
export const CHART_PALETTE = [
  rgbaPrimary(1),
  rgbaPrimary(0.85),
  rgbaPrimary(0.7),
  rgbaPrimary(0.55),
  rgbaPrimary(0.4),
  rgbaPrimary(0.25),
];

export const CHART = {
  areaFill: rgbaPrimary(0.12),
  softIconBg: "#f8eaea",
};

export const PDF_HEADER = {
  fillColor: BRAND.primaryRgb,
  textColor: [255, 255, 255],
};

export const pdfHeaderStyles = {
  fillColor: PDF_HEADER.fillColor,
  textColor: PDF_HEADER.textColor,
};

export const LOADER_COLORS = [`${BRAND.primary}e8`, BRAND.primary];

/** Chapter create/edit color picker defaults */
export const CHAPTER = {
  defaultBackgroundColor: BRAND.primary,
  defaultColor: BRAND.white,
};

/** Shared react-select styles for filter panels — focused/selected use brand + white text */
export const selectFilterStyles = {
  control: (base) => ({ ...base, fontSize: "0.85rem", minHeight: "36px" }),
  placeholder: (base) => ({
    ...base,
    fontSize: "0.85rem",
    color: "#222",
    fontWeight: "500",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "0.85rem",
    color: "#222",
    fontWeight: "500",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.85rem",
    backgroundColor: state.isSelected
      ? BRAND.primary
      : state.isFocused
        ? BRAND.primaryLight
        : undefined,
    color:
      state.isSelected || state.isFocused ? BRAND.textOnPrimary : "#222",
    cursor: "pointer",
  }),
  input: (base) => ({ ...base, fontSize: "0.85rem", color: "#222" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};
